import React, { useState, useRef, useEffect } from "react";
import "./InputBox.css";
import { useTranslation } from "react-i18next";

const InputBoxLarge = ({
  label,
  defaultValue = "",
  onChange,
  type = "text",
  validators = [],
  name,
  disablePastDates,
  textTransform = "none",
  disableFutureDates,
  border = "",
  startDate,
  isRequired = false,
  important = false,
  readOnly = false,
  error,
  numbersOnly = false,
  labelClassName = "",
}) => {
  const [value, setValue] = useState(defaultValue);
  const [errors, setErrors] = useState([]);
  const dateInputRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  // Ensure validators is always an array
  const validatorArray = Array.isArray(validators)
    ? validators
    : validators
      ? [validators]
      : [];

  // Add isRequired validator if required prop is true
  const effectiveValidators = isRequired
    ? [
        ...validatorArray,
        (val) =>
          (val !== null && val !== undefined && val !== "") ||
          "This field is required",
      ]
    : validatorArray;

  const validateInput = (inputValue) => {
    const newErrors = effectiveValidators
      .map((validator) => {
        if (typeof validator === "function") {
          const result = startDate
            ? validator(inputValue, startDate)
            : validator(inputValue);
          return result === true ? null : result;
        }
        return null;
      })
      .filter((error) => typeof error === "string");

    return newErrors;
  };

  const handleKeyPress = (event) => {
    if (numbersOnly) {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];

      if (event.ctrlKey || event.metaKey || allowedKeys.includes(event.key)) {
        return;
      }

      if (!/^[0-9.]$/.test(event.key)) {
        event.preventDefault();
        return;
      }

      if (event.key === "." && value.includes(".")) {
        event.preventDefault();
      }
    }
  };

  const handleInputChange = (event) => {
    let newValue = event.target.value;

    if (textTransform === "lowercase") {
      newValue = newValue.toLowerCase();
    } else if (textTransform === "uppercase") {
      newValue = newValue.toUpperCase();
    }

    if (numbersOnly && type !== "date" && type !== "datetime-local") {
      newValue = newValue.replace(/[^0-9.]/g, "");
      const parts = newValue.split(".");
      if (parts.length > 2) {
        newValue = parts[0] + "." + parts.slice(1).join("");
      }
    }

    const newErrors = validateInput(newValue);
    setValue(newValue);
    setErrors(newErrors);

    if (onChange) {
      onChange({
        ...event,
        target: { ...event.target, value: newValue },
      });
    }
  };

  const handleCalendarIconClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  const getMinDate = () => {
    if (startDate) {
      return startDate;
    } else if (disablePastDates) {
      return type === "datetime-local" ? todayDateTime : today;
    }
    return undefined;
  };

  const getMaxDate = () => {
    if (disableFutureDates) {
      return type === "datetime-local" ? todayDateTime : today;
    }
    return undefined;
  };

  const today = new Date().toISOString().split("T")[0];
  const todayDateTime = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM format
  const displayErrors = error ? [error] : errors;

  // Check if input type should show calendar icon and apply date styles
  const isDateTimeInput = type === "date" || type === "datetime-local";

  return (
    <div className="large-input-box-container posrel">
      <input
        className={`input-box common-text ftw-4 ${
          isDateTimeInput ? "hide-native-icon" : ""
        } ${displayErrors.length > 0 ? "input-error" : ""} ${border}`}
        type={type}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyPress}
        placeholder=""
        ref={isDateTimeInput ? dateInputRef : null}
        name={name}
        readOnly={readOnly}
        min={getMinDate()}
        max={getMaxDate()}
        inputMode={numbersOnly ? "numeric" : undefined}
      />
      <label
        className={`input-date-label input-label common-text ftw-4 ${labelClassName} ${value ? "has-value" : ""} `}
      >
        {t(label)}
        {important && <span className="required-asterisk">*</span>}
      </label>
      {isDateTimeInput && (
        <div className="calendar-icon" onClick={handleCalendarIconClick}>
          <svg
            className="calendor-icon-svg"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              opacity="0.5"
              d="M20 8H0V17C0 17.7956 0.316071 18.5587 0.87868 19.1213C1.44129 19.6839 2.20435 20 3 20H17C17.7956 20 18.5587 19.6839 19.1213 19.1213C19.6839 18.5587 20 17.7956 20 17V8ZM5 6C4.73478 6 4.48043 5.89464 4.29289 5.70711C4.10536 5.51957 4 5.26522 4 5V1C4 0.734784 4.10536 0.48043 4.29289 0.292893C4.48043 0.105357 4.73478 0 5 0C5.26522 0 5.51957 0.105357 5.70711 0.292893C5.89464 0.48043 6 0.734784 6 1V5C6 5.26522 5.89464 5.51957 5.70711 5.70711C5.51957 5.89464 5.26522 6 5 6ZM15 6C14.7348 6 14.4804 5.89464 14.2929 5.70711C14.1054 5.51957 14 5.26522 14 5V1C14 0.734784 14.1054 0.48043 14.2929 0.292893C14.4804 0.105357 14.7348 0 15 0C15.2652 0 15.5196 0.105357 15.7071 0.292893C15.8946 0.48043 16 0.734784 16 1V5C16 5.26522 15.8946 5.51957 15.7071 5.70711C15.5196 5.89464 15.2652 6 15 6Z"
              fill="#6D6D6D"
            />
            <path
              d="M17 2H16V5C16 5.26522 15.8946 5.51957 15.7071 5.70711C15.5196 5.89464 15.2652 6 15 6C14.7348 6 14.4804 5.89464 14.2929 5.70711C14.1054 5.51957 14 5.26522 14 5V2H6V5C6 5.26522 5.89464 5.51957 5.70711 5.70711C5.51957 5.89464 5.26522 6 5 6C4.73478 6 4.48043 5.89464 4.29289 5.70711C4.10536 5.51957 4 5.26522 4 5V2H3C2.20435 2 1.44129 2.31607 0.87868 2.87868C0.316071 3.44129 0 4.20435 0 5V8H20V5C20 4.20435 19.6839 3.44129 19.1213 2.87868C18.5587 2.31607 17.7956 2 17 2Z"
              fill="#454545"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default InputBoxLarge;
